import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/Modal";
import { BadgeCheckIcon, Eye, IdCard, Shield, UserRound, X } from "lucide-react";
import InfoCard from "@/components/shared/InfoCard";
import toast from "react-hot-toast";
import { PermissionsService } from "@/services/permissionsService";
import { groupPermissionsByCategory } from "../permission-category";
import RenderPermissions from "./RenderPermissions";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const ViewRoleModal = ({ open, onModelClose, role }) => {
    // Local state for selected role
    const [current, setCurrent] = useState(null);

    // Permissions grouped by category
    const [permissionsByCategory, setPermissionsByCategory] = useState({});

    // Loading state for permissions fetch
    const [loadingPermissions, setLoadingPermissions] = useState(false);

    // Load role + permissions when modal opens
    useEffect(() => {
        if (!open) return;

        setCurrent(role);

        const loadPermissions = async () => {
            try {
                setLoadingPermissions(true);

                const res = await PermissionsService.fetchPermissionsList({
                    pagination: false,
                    // Only the active catalogue. Codes a coarser grant already
                    // covers are inactive on purpose (see the server's
                    // INTERVIEW_GRANT_EXPANSION) and must not appear as their own
                    // checkbox - a box you tick that is implied anyway, or that
                    // grants nothing on its own, is worse than no box.
                    status: true,
                });

                const permissions = res?.data?.permissions ?? [];

                // Group permissions for UI display
                setPermissionsByCategory(groupPermissionsByCategory(permissions));
            } catch (e) {
                console.error(e);
                toast.error(apiErrorMessageFormat(e, "Failed to load permissions"));
            } finally {
                setLoadingPermissions(false);
            }
        };

        loadPermissions();
    }, [open, role]);

    // Control dialog open state
    const isOpen = open && !!current;

    // Convert role permissions into a Set for fast lookup
    const assignedPermissions = useMemo(() => {
        const list = current?.permissions || [];
        return new Set(list);
    }, [current]);

    // Modal title fallback
    const title = useMemo(() => current?.name || "Role", [current]);

    // Role detail fields for InfoCard rendering
    const details = useMemo(() => {
        return [
            {
                label: "Name",
                value: current?.name || "",
                icon: UserRound,
            },
            {
                label: "Department",
                value: current?.departmentScope || "—",
                icon: Eye,
            },
            {
                label: "Parent Role",
                value: current?.parentRole?.name || "—",
                icon: Shield,
            },
            {
                label: "Description",
                value: current?.description || "—",
                icon: IdCard,
            },
        ];
    }, [current]);

    // Extract category names from grouped permissions
    const categories = useMemo(
        () => Object.keys(permissionsByCategory || {}),
        [permissionsByCategory]
    );

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(v) => {
                // Close modal handler
                if (!v) onModelClose?.();
            }}
        >
            <DialogContent className="w-[90%] md:w-full flex flex-col p-0">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        {/* Modal title */}
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            View Role
                        </DialogTitle>

                        {/* Role name subtitle */}
                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            {title}
                        </DialogDescription>
                    </div>

                    {/* Close button */}
                    <DialogClose asChild>
                        <span className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                            <X size={16} strokeWidth={3} />
                        </span>
                    </DialogClose>
                </DialogHeader>

                {/* Modal body */}
                <div className="space-y-8 p-6 overflow-y-auto custom-scrollbar">

                    {/* Role details section */}
                    <div>
                        <h3 className="mb-5 text-lg font-semibold">Details</h3>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            {details.map((item, idx) => (
                                <InfoCard
                                    key={idx}
                                    icon={item.icon}
                                    label={item.label}
                                    value={item.value}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Permissions section */}
                    <div>
                        <h3 className="mb-5 text-lg font-semibold">Permissions</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 gap-y-4 items-stretch">
                            <RenderPermissions
                                categories={categories}
                                isLoading={loadingPermissions}
                                permissionsByCategory={permissionsByCategory}
                                assignedPermissions={assignedPermissions}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ViewRoleModal;